import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, Col, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterDateTime, FilterSelect, Pager, ServiceThreadSpinner, SortHeader } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, filterHelpers, helpers } from '../utils';

// Matches CurrentOrders.js - the Report Runs grid uses the same client-side Pager as the Customer
// Dashboard's Current Orders grid, so the two pages default to the same page size.
const DEFAULT_PAGE_SIZE = 10;

// Longest a criteria summary is allowed to run before it is cut off - the full JSON is still
// available from the cell's tooltip.
const CRITERIA_SUMMARY_MAX_LENGTH = 120;

// How long a View HTML preview's blob URL is kept alive before it is revoked - long enough for
// the new tab to finish loading it.
const HTML_PREVIEW_REVOKE_DELAY_MS = 60000;

const ALL_USERS_OPTION = { label: "All users", value: "" };
const ALL_REPORTS_OPTION = { label: "All reports", value: "" };

// A single-variant report's endpoint name is set to this literal rather than repeating the report
// name - either way it adds nothing next to the report name, so both are hidden.
const GENERIC_ENDPOINT_PLACEHOLDER = "Report";

// Order Confirmation logs its SOP order type as the endpoint; an unrecognized type is logged as "0"
// (older rows) or "UNKNOWN", and neither tells the reader anything, so the variant line is hidden.
const MEANINGLESS_ENDPOINTS = ["0", "UNKNOWN"];

// FilterSelect shows the option whose value matches props.value.value, so it needs the OPTION
// object - the filters themselves keep the plain value, which is what the request payload sends.
// A null value selects the "All" option (value "").
function optionFor(options, value) {
   return _.find(options, (o) => o.value === (value ?? "")) || null;
}

// Splits a PascalCase identifier into space-separated words for display (CaseLotInventory ->
// "Case Lot Inventory"). An acronym run stays together when it leads into a new word
// (PODetailReport -> "PO Detail Report") instead of splitting every capital letter on its own.
function splitPascalCase(value) {
   if (!value) return "";
   return value
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .trim();
}

// The endpoint is the run's variant (Internal/Customer, AllActive/SingleActive, etc.) - shown only
// when it tells the user something the report name does not: not the generic placeholder, and not
// a repeat of the report name itself (single-variant reports record both the same).
function getVariantLabel(row) {
   if (!row.endpoint
      || row.endpoint === row.reportName
      || row.endpoint === GENERIC_ENDPOINT_PLACEHOLDER
      || MEANINGLESS_ENDPOINTS.includes(row.endpoint)) {
      return null;
   }
   return splitPascalCase(row.endpoint);
}

// Admin Tools > Report Preview (backlog 49.4). Lists the runs recorded in utilities.ReportLog and
// replays any of them from its stored criteria - View HTML (for template authoring) and View PDF
// (what the user got). A replay re-runs the report against today's data and runs as the admin
// viewing it, never as the original user - see the note rendered above the grid.
export default function ReportPreview() {
   const currentUser = helpers.getCurrentUser();
   const currentUserName = currentUser?.fullName ?? "";

   const [data, setData] = useState([]);
   const [filters, setFilters] = useState(() => {
      let today = getToday();
      return [
         { filterName: "StartDate", value: today },
         { filterName: "EndDate", value: today },
         { filterName: "UserName", value: currentUserName },
         { filterName: "ReportName", value: "" },
      ];
   });
   const [loaded, setLoaded] = useState(false);
   const [pending, setPending] = useState({});
   const [queried, setQueried] = useState(false);
   const [reportOptions, setReportOptions] = useState([ALL_REPORTS_OPTION]);
   const [userOptions, setUserOptions] = useState(() => buildUserOptions([], currentUserName));

   // The server already returns every matching row (up to 500) in one call, so paging and sorting
   // for this grid are client-side over that array - unlike Current Orders, which pages server-side.
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
   const [sortField, setSortField] = useState("requestDate");
   const [sortDirection, setSortDirection] = useState("desc");

   // Only the response for the most recently sent request may update the grid - a filter change
   // fires a new request immediately, and an older request still in flight must not win the race
   // and overwrite it with stale rows.
   const latestRequestId = useRef(0);

   useEffect(() => {
      document.title = `Report Preview`;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Runs on mount for the initial load, then again on every filter change - there is no Search
   // button, so a filter change is the only other trigger.
   useEffect(() => {
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filters]);

   function buildUserOptions(names, keepName) {
      let list = _.map(_.uniq(_.compact(names)), (name) => ({ label: name, value: name }));
      if (keepName && !_.some(list, (o) => o.value === keepName)) {
         list.push({ label: keepName, value: keepName });
      }
      let sorted = _.sortBy(list, ["label"]);
      sorted.unshift(ALL_USERS_OPTION);
      return sorted;
   }

   function buildReportOptions(names) {
      let list = _.map(_.uniq(_.compact(names)), (name) => ({ label: name, value: name }));
      let sorted = _.sortBy(list, ["label"]);
      sorted.unshift(ALL_REPORTS_OPTION);
      return sorted;
   }

   function createPayload() {
      return {
         StartDate: filterHelpers.getValue(filters, "StartDate") || getToday(),
         EndDate: filterHelpers.getValue(filters, "EndDate") || getToday(),
         UserName: filterHelpers.getValue(filters, "UserName") || null,
         ReportName: filterHelpers.getValue(filters, "ReportName") || null,
      };
   }

   function getToday() {
      let now = new Date();
      let month = now.getMonth() + 1;
      let day = now.getDate();
      return `${now.getFullYear()}-${month < 10 ? `0${month}` : `${month}`}-${day < 10 ? `0${day}` : `${day}`}`;
   }

   function onFilterChange(newFilterValue) {
      setFilters(filterHelpers.getFilters(filters, newFilterValue));
   }

   function pendingKey(id, action) {
      return `${id}-${action}`;
   }

   function isPending(id, action) {
      return pending[pendingKey(id, action)] === true;
   }

   function setRowPending(id, action, value) {
      setPending((prior) => ({ ...prior, [pendingKey(id, action)]: value }));
   }

   function runQuery() {
      const requestId = ++latestRequestId.current;
      setLoaded(false);
      api
         .post("Report/GetReportLog", createPayload())
         .then((response) => {
            if (requestId !== latestRequestId.current) return;
            if (response.data.success) {
               setData(response.data.rows || []);
               setReportOptions(buildReportOptions(response.data.reportNames));
               setUserOptions(buildUserOptions(response.data.userNames, currentUserName));
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            if (requestId !== latestRequestId.current) return;
            api.catchHandler(error);
         })
         .finally(() => {
            if (requestId !== latestRequestId.current) return;
            setLoaded(true);
            setQueried(true);
         });
   }

   // A new set of rows or a new page size invalidates whatever page the user was on.
   useEffect(() => {
      setPage(1);
   }, [data, pageSize]);

   const sortedData = useMemo(
      () => _.orderBy(data, [sortField], [sortDirection]),
      [data, sortField, sortDirection]
   );
   const totalPages = useMemo(() => Math.max(1, Math.ceil(sortedData.length / pageSize)), [sortedData, pageSize]);
   const pageData = useMemo(
      () => sortedData.slice((page - 1) * pageSize, page * pageSize),
      [sortedData, page, pageSize]
   );

   function onPageChange(newPage) {
      setPage(newPage);
   }

   function onPageSizeChange(newPageSize) {
      setPageSize(newPageSize);
   }

   // New column: always start ascending. Same column: use the direction SortHeader already toggled to.
   // Either way go back to page 1, as CurrentOrders does after every sort.
   function onSortChange(field, direction) {
      if (field !== sortField) {
         setSortField(field);
         setSortDirection("asc");
      } else {
         setSortDirection(direction);
      }
      setPage(1);
   }

   // View HTML: the server returns the composed report as text/html on success, or the standard
   // JSON failure body on a replay error - both arrive with an HTTP 200, so the response's own
   // content type is what tells them apart (never open a JSON error body as a page).
   function viewHtml(row) {
      if (isPending(row.id, "html")) return;
      setRowPending(row.id, "html", true);
      api
         .post("Report/PreviewReportHtml", { Id: row.id })
         .then((response) => {
            const contentType = response.headers?.["content-type"] || "";
            if (contentType.indexOf(constants.MIME_TYPES.html) === 0) {
               const blob = new Blob([response.data], { type: constants.MIME_TYPES.html });
               const blobUrl = URL.createObjectURL(blob);
               const opened = window.open(blobUrl, "_blank");
               if (!opened) {
                  toast.error("Your browser blocked the preview tab. Allow popups for this site and try again.");
               }
               setTimeout(() => URL.revokeObjectURL(blobUrl), HTML_PREVIEW_REVOKE_DELAY_MS);
            } else {
               toast.error(response.data?.message || "Unable to preview this report.");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("Unable to preview this report.");
         })
         .finally(() => setRowPending(row.id, "html", false));
   }

   function viewPdf(row) {
      if (isPending(row.id, "pdf")) return;
      setRowPending(row.id, "pdf", true);
      let exportName = `${row.reportName}_${row.id}_${getToday()}`;
      api
         .postDownloadBinary(
            "Report/PreviewReportPdf",
            { Id: row.id },
            constants.MIME_TYPES.pdf,
            exportName,
            "There was an error rendering this report."
         )
         // postDownloadBinary never rejects: every failure - a JSON error body, an empty file, a
         // non-2xx response - resolves as { data: false, message }, so that is the one path to check.
         .then((response) => {
            if (response?.data === false) {
               toast.error(response.message);
            }
         })
         .finally(() => setRowPending(row.id, "pdf", false));
   }

   return (
      <DesktopContainer screenName="Report Preview">
         <div className="report-preview-page">
            <div className="filter-header report-preview-filters">
               <Row className="g-3">
                  <Col xs={12} sm={6} lg={2} className="text-white">
                     <FilterDateTime
                        filterName="StartDate"
                        displayName="Start Date"
                        value={filterHelpers.getValue(filters, "StartDate")}
                        onChangeCallback={onFilterChange}
                     />
                  </Col>
                  <Col xs={12} sm={6} lg={2} className="text-white">
                     <FilterDateTime
                        filterName="EndDate"
                        displayName="End Date"
                        value={filterHelpers.getValue(filters, "EndDate")}
                        onChangeCallback={onFilterChange}
                     />
                  </Col>
                  <Col xs={12} sm={6} lg={4} className="text-white">
                     <FilterSelect
                        filterName="UserName"
                        displayName="User"
                        options={userOptions}
                        value={optionFor(userOptions, filterHelpers.getValue(filters, "UserName"))}
                        onChangeCallback={onFilterChange}
                     />
                  </Col>
                  <Col xs={12} sm={6} lg={4} className="text-white">
                     <FilterSelect
                        filterName="ReportName"
                        displayName="Report"
                        options={reportOptions}
                        value={optionFor(reportOptions, filterHelpers.getValue(filters, "ReportName"))}
                        onChangeCallback={onFilterChange}
                     />
                  </Col>
               </Row>
               <Row className="mt-3">
                  <Col className="text-white text-center">
                     Replays re-run the report against today's data, as you, using the run's saved filters.
                     HTML previews look best in Microsoft Edge.
                  </Col>
               </Row>
            </div>

            <Card className="dashboard-card">
               <CardBody>
                  <div>
                     <span className="h2 me-2">Report Runs</span>{" "}
                     <span className="text-muted">{queried ? `${data.length} shown, newest first` : ""}</span>
                  </div>
                  <hr />
                  <ServiceThreadSpinner on={!loaded}>
                  {data && data.length > 0 ? (
                     <>
                     <Row className="align-items-center mb-1">
                        <Col className="transparent-background">
                           <Pager
                              callBack={onPageChange}
                              sizeCallback={onPageSizeChange}
                              pageNumber={page}
                              pageSize={pageSize}
                              totalPages={totalPages}
                              allowAll
                              defaultPageSize={DEFAULT_PAGE_SIZE}
                           />
                        </Col>
                     </Row>
                     <Table size="sm" className="data-table">
                        <thead className="table-header">
                           <tr>
                              <th style={{ width: "140px" }}>
                                 <SortHeader
                                    displayName="Time"
                                    field="requestDate"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "requestDate"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th style={{ width: "150px" }}>
                                 <SortHeader
                                    displayName="User"
                                    field="userName"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "userName"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th style={{ width: "200px" }}>
                                 <SortHeader
                                    displayName="Report"
                                    field="reportName"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "reportName"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>Criteria</th>
                              <th style={{ width: "170px" }}>Preview</th>
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(pageData, (row, index) => (
                              <ReportPreviewRow
                                 key={row.id}
                                 row={row}
                                 index={index}
                                 isPending={isPending}
                                 viewHtml={viewHtml}
                                 viewPdf={viewPdf}
                              />
                           ))}
                        </tbody>
                     </Table>
                     </>
                  ) : (
                     queried ? <div className="text-center my-3">No report runs match these filters.</div> : null
                  )}
                  </ServiceThreadSpinner>
               </CardBody>
            </Card>
         </div>
      </DesktopContainer>
   );
}


function ReportPreviewRow({ row, index, isPending, viewHtml, viewPdf }) {
   const variant = getVariantLabel(row);
   return (
      <tr className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
         <td className="data-cell">
            {dateHelpers.dateTimeFormat(row.requestDate, "MM-DD-YYYY HH:mm")}
            {row.succeeded ? null : <span className="report-preview-badge-failed">Failed</span>}
         </td>
         <td className="data-cell">{row.userName}</td>
         <td className="data-cell">
            {splitPascalCase(row.reportName)}
            {variant ? <><br /><small className="report-preview-secondary-text">{variant}</small></> : null}
         </td>
         <td className="data-cell" title={row.criteria}>{summarizeCriteria(row.criteria)}</td>
         <td className="data-cell">
            {row.replayable ? (
               <div className="d-flex flex-wrap gap-2 report-preview-actions">
                  <Button
                     className="shredLink transparent-background"
                     disabled={isPending(row.id, "html")}
                     onClick={() => viewHtml(row)}
                  >
                     View HTML
                  </Button>
                  <Button
                     className="shredLink transparent-background"
                     disabled={isPending(row.id, "pdf")}
                     onClick={() => viewPdf(row)}
                  >
                     View PDF
                  </Button>
               </div>
            ) : (
               <span className="report-preview-secondary-text" title={row.notReplayableReason}>
                  Not replayable
                  {row.notReplayableReason ? <><br /><small>{row.notReplayableReason}</small></> : null}
               </span>
            )}
         </td>
      </tr>
   );
}


// Non-empty, non-zero, non-false fields of the stored criteria JSON as "Field: value" pairs -
// the full JSON stays available in the cell's tooltip for anything this summary drops or shortens.
function summarizeCriteria(criteriaJson) {
   if (!criteriaJson) return "";
   let parsed;
   try {
      parsed = JSON.parse(criteriaJson);
   } catch {
      // Unparseable criteria are shown raw, under the same length cap as a summary.
      return clampSummary(criteriaJson);
   }
   let parts = [];
   _.forEach(parsed, (value, key) => {
      if (value === null || value === undefined || value === "" || value === false || value === 0) return;
      if (Array.isArray(value)) {
         if (value.length === 0) return;
         parts.push(`${key}: ${value.join(", ")}`);
      } else if (typeof value === "object") {
         parts.push(`${key}: ${JSON.stringify(value)}`);
      } else {
         parts.push(`${key}: ${value}`);
      }
   });
   return clampSummary(parts.join(", "));
}

function clampSummary(text) {
   return text.length > CRITERIA_SUMMARY_MAX_LENGTH
      ? `${text.slice(0, CRITERIA_SUMMARY_MAX_LENGTH)}...`
      : text;
}
